<script setup>
import { ref } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

defineProps({
  data: {
    type: Object,
    default: () => ({ templates: [], generatedAt: null }),
  },
  loading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});
const emit = defineEmits(['refresh']);

const TYPE_LABEL = {
  apoio: 'Apoio',
  cobranca: 'Cobrança',
  critica: 'Crítica',
  ataque: 'Ataque',
  duvida: 'Dúvida',
  elogio: 'Elogio',
  imprensa: 'Imprensa',
  mobilizacao: 'Mobilização',
};

const TONE_LABEL = {
  institucional: 'Institucional',
  popular: 'Popular',
  firme: 'Firme',
  empatico: 'Empático',
  tecnico: 'Técnico',
};

const ACTION_META = {
  responder_publico: { label: 'Responder em público', color: 'bg-emerald-100 text-emerald-700', icon: 'i-lucide-message-circle' },
  levar_inbox: { label: 'Levar para o inbox', color: 'bg-blue-100 text-blue-700', icon: 'i-lucide-inbox' },
  nao_responder: { label: 'Não responder', color: 'bg-slate-100 text-slate-600', icon: 'i-lucide-message-circle-off' },
  escalar_coordenacao: { label: 'Escalar para coordenação', color: 'bg-amber-100 text-amber-700', icon: 'i-lucide-arrow-up-right' },
};

const RISK_META = {
  baixo: 'bg-emerald-100 text-emerald-700',
  medio: 'bg-amber-100 text-amber-700',
  alto: 'bg-rose-100 text-rose-700',
  critico: 'bg-rose-200 text-rose-900',
};

const expanded = ref(new Set());
const activeTones = ref({});

const toggle = type => {
  if (expanded.value.has(type)) expanded.value.delete(type);
  else expanded.value.add(type);
  expanded.value = new Set(expanded.value);
};
const isOpen = type => expanded.value.has(type);

const setTone = (type, tone) => {
  activeTones.value = { ...activeTones.value, [type]: tone };
};
const currentTone = type => activeTones.value[type] || 'institucional';

const copy = async text => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (e) {
    // ignore
  }
};
</script>

<template>
  <div class="rounded-xl border border-n-strong/40 bg-n-background p-4 shadow-sm">
    <div class="mb-3 flex items-center justify-between">
      <div>
        <h3 class="text-sm font-semibold text-n-slate-12">Biblioteca de Respostas</h3>
        <p class="mt-0.5 text-[11px] text-n-slate-11">
          Respostas prontas em 5 tons + ação recomendada por tipo de comentário.
        </p>
      </div>
      <Button
        variant="outline"
        size="sm"
        icon="i-lucide-message-square-reply"
        :is-loading="loading"
        :disabled="disabled"
        @click="emit('refresh')"
      >
        Gerar
      </Button>
    </div>

    <div v-if="disabled" class="py-6 text-center text-sm text-n-slate-11">
      Selecione um perfil para gerar a biblioteca.
    </div>

    <div v-else-if="loading && !data.templates?.length" class="flex justify-center py-8">
      <Spinner />
    </div>

    <div
      v-else-if="!data.templates?.length"
      class="py-6 text-center text-sm text-n-slate-11"
    >
      Clique em "Gerar" para criar respostas para os 8 tipos canônicos.
    </div>

    <div v-else class="space-y-2">
      <div
        v-for="t in data.templates"
        :key="t.commentType"
        class="rounded-lg border border-n-weak bg-n-background"
      >
        <button
          type="button"
          class="flex w-full items-center justify-between gap-3 p-3 text-left hover:bg-n-slate-2"
          @click="toggle(t.commentType)"
        >
          <div class="flex items-center gap-2">
            <span :class="[ACTION_META[t.recommendedAction]?.icon, 'size-4 text-n-slate-12']" />
            <div>
              <p class="text-sm font-bold text-n-slate-12">
                {{ TYPE_LABEL[t.commentType] || t.commentType }}
              </p>
              <p class="text-[10px] text-n-slate-11">{{ t.rationale }}</p>
            </div>
          </div>
          <div class="flex items-center gap-1.5">
            <span :class="[RISK_META[t.riskLevel], 'rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase']">
              Risco {{ t.riskLevel }}
            </span>
            <span :class="[ACTION_META[t.recommendedAction]?.color, 'rounded px-1.5 py-0.5 text-[9px] font-semibold']">
              {{ ACTION_META[t.recommendedAction]?.label }}
            </span>
            <span :class="isOpen(t.commentType) ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" class="size-4 text-n-slate-11" />
          </div>
        </button>

        <div v-if="isOpen(t.commentType)" class="border-t border-n-weak p-3">
          <!-- Tone tabs -->
          <div class="mb-3 flex flex-wrap gap-1">
            <button
              v-for="(text, tone) in t.tones"
              :key="tone"
              type="button"
              class="rounded-md px-2.5 py-1 text-[11px] font-semibold transition"
              :class="
                currentTone(t.commentType) === tone
                  ? 'bg-blue-600 text-white'
                  : 'bg-n-slate-3 text-n-slate-11 hover:text-n-slate-12'
              "
              @click="setTone(t.commentType, tone)"
            >
              {{ TONE_LABEL[tone] || tone }}
            </button>
          </div>

          <!-- Active tone response -->
          <div class="rounded-md border border-n-weak bg-n-slate-2 p-3">
            <p class="mb-2 text-sm leading-relaxed text-n-slate-12">
              {{ t.tones[currentTone(t.commentType)] || '—' }}
            </p>
            <Button
              variant="outline"
              size="sm"
              icon="i-lucide-copy"
              @click="copy(t.tones[currentTone(t.commentType)])"
            >
              Copiar
            </Button>
          </div>

          <!-- Examples -->
          <div v-if="t.examples?.length" class="mt-3">
            <p class="mb-1 text-[10px] font-semibold uppercase tracking-wide text-n-slate-11">
              Exemplos reais
            </p>
            <ul class="space-y-1">
              <li
                v-for="(ex, i) in t.examples"
                :key="i"
                class="rounded bg-n-slate-2 p-2 text-[11px] italic text-n-slate-11"
              >
                "{{ ex }}"
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
